import Text from "common/Typography/Text";
import Modal from "common/Modal/Modal";
import React from "react";
import {
  ClientData,
  ClientStatus,
  LanguageNameSpaces,
  WorkspaceType,
} from "@shared/types";
import { OPEN_TRADES_PATH } from "common/constants";
import { displayResultMessage } from "utils/misc";
import RouterLink from "common/Buttons/RouterLink";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useChangeAccountStatus } from "@shared/features/configuration/hooks.ts";
import { useSelectRealUser } from "@shared/utils/hooks/misc.ts";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { capitalizeFirstLetter } from "@shared/common/utils.ts";
import { joinWords } from "@shared/common/i18n/utils.ts";

interface DeactivateAccountModalProps {
  accountStatus?: ClientStatus;
  onCancel: () => void;
  onDelete?: () => void;
  account?: ClientData;
}

const DeactivateAccountModal = ({
  accountStatus,
  onCancel,
  onDelete,
  account,
}: DeactivateAccountModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);

  const user = useSelectRealUser();
  const { isLoading, handleChangeClientStatus } = useChangeAccountStatus(() => {
    onCancel();
    if (accountStatus === ClientStatus.DELETED) {
      onDelete?.();
    }
  }, displayResultMessage);
  const workspace = useSelector(selectWorkspace);
  const isDemo = workspace === WorkspaceType.DEMO;
  const clientId = account?.client_id;

  const isDelete = accountStatus === ClientStatus.DELETED;
  const actionName = isDelete
    ? t("deactivate_modal.delete")
    : t("deactivate_modal.deactivate");
  const titleActionName = capitalizeFirstLetter(actionName);
  let texts: string[];
  const isAssetManagerDeleteWarning =
    !!account?.permission_level && !user?.is_sub_user;
  if (isAssetManagerDeleteWarning) {
    texts = [
      t("deactivate_modal.sub_user_disconnect"),
      "",
      "",
      t("deactivate_modal.sub_user_trades"),
      t("deactivate_modal.sub_user_delete_confirm"),
    ];
  } else {
    texts = [
      isDelete
        ? t("deactivate_modal.text_1_delete")
        : t("deactivate_modal.text_1_deactivate"),
      t("deactivate_modal.text_2"),
      isDelete
        ? t("deactivate_modal.text_3_delete")
        : t("deactivate_modal.text_3_deactivate"),
      isDelete
        ? t("deactivate_modal.text_4_delete")
        : t("deactivate_modal.text_4_deactivate"),
    ];
    if (isDelete) {
      texts.splice(3, 0, t("deactivate_modal.text_irreversible"));
    }
  }

  const onOk = () =>
    handleChangeClientStatus(clientId ? [clientId] : [], accountStatus);

  const sectionPadding = 16;

  return (
    <Modal
      open={!!accountStatus}
      title={joinWords([titleActionName, t("deactivate_modal.account")])}
      onOk={onOk}
      onCancel={onCancel}
      okText={titleActionName}
      centered={true}
      okButtonProps={{ loading: isLoading }}
    >
      <Text>{texts[0]}</Text>
      {!isDemo && (
        <Text
          as="div"
          style={{ marginTop: sectionPadding, fontStyle: "italic" }}
        >
          <Text style={{ display: "inline" }}>{texts[1]}</Text>
          {!isAssetManagerDeleteWarning && (
            <RouterLink to={OPEN_TRADES_PATH} style={{ display: "inline" }}>
              {t("common.open_trades")}
            </RouterLink>
          )}
          {texts[2] && <Text style={{ display: "inline" }}>{texts[2]}</Text>}
        </Text>
      )}
      <Text style={{ fontWeight: "600", marginTop: sectionPadding }}>
        {texts[3]}
      </Text>
      {!!texts[4] && (
        <Text style={{ fontWeight: "600", marginTop: sectionPadding }}>
          {texts[4]}
        </Text>
      )}
    </Modal>
  );
};

export default DeactivateAccountModal;
